// Google Places autocomplete on a plain <input> instead of @roc/ui's
// AutocompleteAddressFieldV2, whose outlined TextField doesn't match this design's pill look.
import { useEffect, useRef } from 'react';

export const AddressAutocompleteInput = ({
  className,
  placeholder,
  value,
  onChange,
}: {
  className?: string;
  placeholder?: string;
  value: string;
  onChange: (event: { target: { value: string; fullInfo?: any } }) => void;
}) => {
  const inputId = useRef(`address-autocomplete-${Math.random().toString(36).slice(2)}`).current;
  const autocompleteRef = useRef<any>(null);

  // Parents pass a new onChange every render; read it through a ref so the Places
  // instance isn't torn down (and its suggestion list removed) on every keystroke.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    let retryHandle: any = null;
    let attempts = 0;

    const attach = () => {
      const google = (window as any).google;
      const inputEl = document.getElementById(inputId) as HTMLInputElement;
      if (!google?.maps?.places || !inputEl) return false;
      if (autocompleteRef.current) return true;

      const autocomplete = new google.maps.places.Autocomplete(inputEl, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
      });
      autocomplete.setFields(['address_component', 'formatted_address', 'geometry']);

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place?.address_components) {
          onChangeRef.current({ target: { value: place.formatted_address, fullInfo: place } });
        }
        // No focus() here — it re-opens the Places suggestion list over the picked address.
      });

      autocompleteRef.current = autocomplete;
      return true;
    };

    // The Maps script can finish loading after mount; retry briefly instead of giving up.
    if (!attach()) {
      retryHandle = setInterval(() => {
        attempts += 1;
        if (attach() || attempts >= 50) clearInterval(retryHandle);
      }, 200);
    }

    // Google keeps a listener on the autocomplete
    return () => {
      if (retryHandle) clearInterval(retryHandle);
      const google = (window as any).google;
      if (autocompleteRef.current && google?.maps?.event) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      document.querySelectorAll('.pac-container').forEach((node) => node.remove());
    };
  }, [inputId]);

  return (
    <input
      id={inputId}
      className={className}
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChangeRef.current({ target: { value: event.target.value } })}
      autoComplete="off"
    />
  );
};
